import type { SetStateAction } from "react";
import { useCallback, useEffect, useState } from "react";
import { css, styled } from "styled-components";

import { CrossIcon, MagicWandIcon, RedCircleErrorIcon, UIButton } from "common/components";
import { mediaStyles } from "common/styles";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { exitAIGeneratedMap, updateAIGeneratedMapProgress } from "domains/createMapDetail/actions";
import { getAIGeneratedMapErrorCode } from "domains/createMapDetail/selectors";
import { getAIGeneratedTopicDetails } from "domains/topicDetail/actions";
import { errorCodes } from "domains/topicDetail/actionTypes";
import { AIPromptProgress } from "domains/topicDetail/components/AIPrompt/AIPromptProgress";

const StyledPrompt = styled.div`
  position: absolute;
  top: 110px;
  left: 2rem;
  right: 3rem;
  background-color: ${({ theme }) => theme.colors.backgroundSecondary};
  color: ${({ theme }) => theme.colors.textBright};
  padding: 1rem 1.5rem 1rem 2.25rem;
  ${mediaStyles(({ spacing }) => `right: ${spacing}rem`)};

  &:before {
    content: "";
    left: 1rem;
    top: 1rem;
    bottom: 1rem;
    position: absolute;
    background-color: ${({ theme }) => theme.colors.inputBorder}AA;
    width: 4px;
    border-radius: 2px;
  }
`;

const StyledPromptTitleContainer = styled.div`
  margin: 0 0 1rem;
  align-items: center;
  width: 100%;
  display: inline-flex;
  justify-content: space-between;
`;

const StyledPromptTitle = styled.h2`
  margin: 0 0 1rem;
  align-items: center;
  display: inline-flex;
  gap: 1.25rem;
`;

const StyledInputRow = styled.div`
  display: inline-flex;
  gap: 1rem;
  width: 100%;
  align-items: flex-end;
`;

const StyledPromptDescription = styled.div`
  line-height: 1.6rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.textBright};
  margin-bottom: 0.5rem;
`;

const StyledPromptConstrains = styled.div`
  line-height: 1.6rem;
  color: ${({ theme }) => theme.colors.textFade};
  margin-bottom: 1rem;
`;

const StyledInputContainer = styled.div`
  position: relative;
  width: 100%;
`;

const StyledInputCounter = styled.div<{ charactersLeft: number }>`
  position: absolute;
  right: 10px;
  top: 10px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  ${({ theme, charactersLeft }) => css`
    background:
      radial-gradient(closest-side, ${theme.colors.backgroundSecondary} 79%, transparent 80% 100%),
      conic-gradient(${theme.colors.textBright} ${charactersLeft}%, ${theme.colors.textFade}AA 0);
  `}
`;

const StyledInput = styled.input<{ error: boolean; isEnoughCharacters: boolean }>`
  transition: border 0.3s;
  width: 100%;
  height: 40px;
  background: transparent;
  font-size: 1rem;
  box-sizing: border-box;
  outline: none;
  border-radius: 5px;
  appearance: none;
  border-width: 1px;
  border-style: solid;
  ${({ theme, error, isEnoughCharacters }) => css`
    padding: 10px ${isEnoughCharacters ? "10px" : "40px"} 10px 14px;
    border-color: ${error ? theme.colors.red : theme.colors.textFade};
    color: ${theme.colors.textBright};
  `};
`;

const StyledRedCircleErrorIcon = styled(RedCircleErrorIcon)`
  width: 40px;
  color: ${({ theme }) => theme.colors.fillPrimary};
`;

const StyledError = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const StyledErrorText = styled.div`
  font-weight: bold;
  color: ${({ theme }) => theme.colors.textBright};
`;

const StyledCrossIcon = styled(CrossIcon)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.textFade};
  cursor: pointer;
  margin-left: 3rem;
  transition: all 0.3s;
  border-radius: 20px;
  padding: 10px;

  &:hover {
    color: ${({ theme }) => theme.colors.textBright};
    background: ${({ theme }) => theme.colors.modalCardBackground};
  }
`;

export const AIPrompt = () => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const [prompt, setPrompt] = useState("");
  const [isPromptValid, setIsPromptValid] = useState(false);
  const errorCode = useSelector(getAIGeneratedMapErrorCode);
  const isEnoughCharacters = prompt.replace(/ /g, "").length >= 50;
  const charactersLeft = (prompt.replace(/ /g, "").length * 100) / 50;

  const onGenerateAction = useCallback(() => {
    dispatch(getAIGeneratedTopicDetails({ prompt }));
    dispatch(updateAIGeneratedMapProgress());
  }, [dispatch, prompt]);

  const onPromptTextChange = (event: { target: { value: SetStateAction<string> } }) => {
    setPrompt(event.target.value);
  };

  const exitAIGeneratedHandle = useCallback(() => {
    dispatch(exitAIGeneratedMap());
  }, [dispatch]);

  useEffect(() => {
    if (!prompt.length || !isEnoughCharacters || prompt.length > 600) {
      setIsPromptValid(false);
    } else {
      setIsPromptValid(true);
    }
  }, [prompt, isEnoughCharacters]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      switch (event.code) {
        case "Escape":
          exitAIGeneratedHandle();
          break;
        case "Enter":
          if (!isPromptValid) return;
          onGenerateAction();
          break;
        default:
          break;
      }
    };
    window.addEventListener("keyup", handleKeyPress);
    return () => {
      window.removeEventListener("keyup", handleKeyPress);
    };
  }, [exitAIGeneratedHandle, onGenerateAction, isPromptValid]);

  return (
    <StyledPrompt>
      <StyledPromptTitleContainer>
        <StyledPromptTitle>
          <MagicWandIcon />
          {translate("app.ai_map.generate_map")}
        </StyledPromptTitle>
        <StyledCrossIcon onClick={exitAIGeneratedHandle} />
      </StyledPromptTitleContainer>
      <StyledPromptDescription>{translate("app.ai_map.description")}</StyledPromptDescription>
      <StyledPromptConstrains>{translate("app.ai_map.requirements")}</StyledPromptConstrains>
      <StyledInputRow>
        <StyledInputContainer>
          <StyledInput
            value={prompt}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onChange={onPromptTextChange}
            name={translate("app.ai_map.input")}
            error={!!errorCode}
            isEnoughCharacters={isEnoughCharacters}
          />
          {!isEnoughCharacters && <StyledInputCounter charactersLeft={charactersLeft} />}
        </StyledInputContainer>
        <UIButton onClick={onGenerateAction} variant="filled" disabled={!isPromptValid}>
          {translate("app.ai_map.generate_button")}
        </UIButton>
      </StyledInputRow>
      {errorCode && (
        <StyledError>
          <StyledRedCircleErrorIcon />
          <StyledErrorText>
            {errorCode === (errorCodes.timeoutExceed as number)
              ? translate("app.ai_map.error.timeout")
              : translate("app.ai_map.error.not_enough_info")}
          </StyledErrorText>
        </StyledError>
      )}
      <AIPromptProgress />
    </StyledPrompt>
  );
};
